import { useState } from 'react'
import { parseUTCDate } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { FilePlus2, Mail, MapPin, Phone, Plus, Search, Trash2 } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CustomerThreads } from '@/features/customer-comms/customer-threads'
import {
  CustomerInvoicesTable,
  CustomerPaymentsGlance,
  CustomerPaymentsList,
  CustomerProjects,
} from '@/features/customers/customer-payments'
import { CustomerGlance, CustomerOverview } from '@/features/customers/customer-insights'
import { useCustomerInsights, useCustomerPayments } from '@/features/customers/api'
import { NewInvoiceDialog } from '@/features/receivables/new-invoice-dialog'
import { useCustomerGroup, useCustomers, useAddGroupMember, useRemoveGroupMember } from '@/api/hooks'
import { BackButton } from '@/components/back-button'
import { LoadingButton } from '@/components/loading-button'
import { toast } from 'sonner'
import { usePageTitle } from '@/lib/page-title'
import { SearchInput } from '@/components/search-input'
import { useCustomerWarrantyClaims } from '@/features/warranty-claims/api'
import { WarrantyClaimsSection } from '@/features/warranty-claims/warranty-claims-section'

export const CUSTOMER_TABS = ['overview', 'projects', 'comms', 'payments', 'claims', 'contacts'] as const
export type CustomerTab = (typeof CUSTOMER_TABS)[number]

interface CustomerGroupDetailPageProps {
  groupId: string
  tab?: CustomerTab
  onTabChange?: (tab: CustomerTab) => void
}

export function CustomerGroupDetailPage({ groupId, tab, onTabChange }: CustomerGroupDetailPageProps) {
  const { data: group, isLoading } = useCustomerGroup(groupId)
  const { data: payments } = useCustomerPayments(groupId)
  const { data: insights } = useCustomerInsights(groupId)
  const { data: warrantyClaims } = useCustomerWarrantyClaims(groupId)
  usePageTitle(group?.name)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [customerSearch, setCustomerSearch] = useState('')
  const { data: customersData } = useCustomers({
    search: customerSearch || undefined,
    limit: 10,
  })
  const addMember = useAddGroupMember(groupId)
  const removeMember = useRemoveGroupMember(groupId)

  const handleAddMember = (customerId: number) => {
    addMember.mutate(
      { shopify_customer_id: customerId, role: 'member' },
      {
        onSuccess: () => {
          toast.success('Member added')
          setAddDialogOpen(false)
          setCustomerSearch('')
        },
        onError: () => toast.error('Failed to add member'),
      },
    )
  }

  const handleRemoveMember = (memberId: number) => {
    removeMember.mutate(memberId, {
      onError: () => toast.error('Failed to remove member'),
    })
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!group) {
    return (
      <div className="mx-auto max-w-7xl">
        <p className="text-muted-foreground">Customer group not found</p>
      </div>
    )
  }

  const address = [
    group.address1,
    group.address2,
    [group.city, [group.state, group.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header>
        <BackButton fallback={{ to: '/customers' }} fallbackLabel="Back to Customers" />
        <div className="mt-1 flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
            <h1 className="text-2xl font-bold tracking-tight">{group.name}</h1>
            {payments ? (
              <Badge variant={payments.paid_projects > 0 ? 'default' : 'secondary'}>
                {payments.paid_projects > 0 ? 'Repeat customer' : 'First-time customer'}
              </Badge>
            ) : null}
          </div>
          <NewInvoiceDialog
            source={{ customerGroupId: group.id }}
            trigger={
              <Button className="shrink-0" size="sm">
                <FilePlus2 aria-hidden="true" />
                New invoice
              </Button>
            }
          />
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {group.company_name && group.company_name !== group.name ? <span>{group.company_name}</span> : null}
          {group.email ? (
            <span className="inline-flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="sr-only">Email</span>
              {group.email}
            </span>
          ) : null}
          {group.phone ? (
            <span className="inline-flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="sr-only">Phone</span>
              {group.phone}
            </span>
          ) : null}
          <span className="inline-flex min-w-0 items-center gap-1.5">
            {address ? (
              <>
                <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="sr-only">Address</span>
                {address}
              </>
            ) : (
              'No address on file'
            )}
          </span>
        </div>
        {group.notes ? <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{group.notes}</p> : null}
      </header>

      {insights && payments ? (
        <CustomerGlance insights={insights} projects={payments.projects} />
      ) : (
        <Skeleton className="h-40 w-full" />
      )}

      <Tabs
        value={tab}
        defaultValue="overview"
        onValueChange={onTabChange ? (value) => onTabChange(value as CustomerTab) : undefined}
        className="pt-2"
      >
        <TabsList variant="line" className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="projects">Projects ({payments?.projects.length ?? group.projects?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="comms">Comms</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="claims">Claims ({warrantyClaims?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="contacts">Contacts ({group.members?.length ?? 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4">
          {insights && payments ? (
            <CustomerOverview insights={insights} projects={payments.projects} />
          ) : (
            <Skeleton className="h-80 w-full" />
          )}
        </TabsContent>
        <TabsContent value="projects" className="mt-4">
          {payments ? (
            <CustomerProjects customerGroupId={group.id} projects={payments.projects} />
          ) : (
            <Skeleton className="h-40 w-full" />
          )}
        </TabsContent>
        <TabsContent value="comms" className="mt-4">
          <CustomerThreads customerGroupId={group.id} customerGroupUuid={group.uuid} />
        </TabsContent>
        <TabsContent value="payments" className="mt-4 space-y-6">
          {payments ? (
            <>
              <CustomerPaymentsGlance payments={payments} uuid={groupId} />
              <section aria-labelledby="customer-invoices" className="space-y-3">
                <h2 id="customer-invoices" className="text-base font-semibold">
                  Invoices
                </h2>
                <CustomerInvoicesTable invoices={payments.invoices} />
              </section>
              <section aria-labelledby="customer-payment-history" className="space-y-3">
                <h2 id="customer-payment-history" className="text-base font-semibold">
                  Payment history
                </h2>
                <CustomerPaymentsList payments={payments.payments} />
              </section>
            </>
          ) : (
            <Skeleton className="h-40 w-full" />
          )}
        </TabsContent>
        <TabsContent value="claims" className="mt-4">
          {warrantyClaims?.length ? (
            <WarrantyClaimsSection claims={warrantyClaims} titled={false} />
          ) : (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {warrantyClaims ? 'No warranty claims' : 'Loading…'}
            </p>
          )}
        </TabsContent>
        <TabsContent value="contacts" className="mt-4">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="sr-only">Contacts</h3>
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="mr-2 h-3 w-3" />
                Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Contact</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <SearchInput
                    placeholder="Search customers..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="max-h-64 space-y-1 overflow-y-auto">
                  {customersData?.items?.map((customer) => (
                    <LoadingButton
                      key={customer.id}
                      variant="ghost"
                      className="w-full justify-start"
                      loading={addMember.isPending && addMember.variables?.shopify_customer_id === customer.id}
                      loadingText="Adding..."
                      onClick={() => handleAddMember(customer.id)}
                    >
                      <div className="text-left">
                        <p className="text-sm font-medium">{customer.display_name || customer.email}</p>
                        {customer.default_address_company && (
                          <p className="text-xs text-muted-foreground">{customer.default_address_company}</p>
                        )}
                      </div>
                    </LoadingButton>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        {/* Desktop table */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Added</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {group.members?.length ? (
                group.members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">
                      {member.shopify_customer?.display_name || `Customer #${member.shopify_customer_id}`}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {member.shopify_customer?.email || '-'}
                    </TableCell>
                    <TableCell>{member.role || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {parseUTCDate(member.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <LoadingButton
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        loading={removeMember.isPending && removeMember.variables === member.id}
                        onClick={() => handleRemoveMember(member.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </LoadingButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No members
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Mobile cards */}
        <div className="space-y-2 md:hidden">
          {group.members?.length ? (
            group.members.map((member) => (
              <div key={member.id} className="flex items-start gap-2 rounded-lg border bg-card p-3">
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-medium">
                    {member.shopify_customer?.display_name || `Customer #${member.shopify_customer_id}`}
                  </p>
                  {member.shopify_customer?.email && (
                    <p className="mt-0.5 break-all text-xs text-muted-foreground">
                      {member.shopify_customer.email}
                    </p>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                    {member.role && <span className="font-medium text-foreground/70">{member.role}</span>}
                    <span>Added {parseUTCDate(member.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <LoadingButton
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-destructive"
                  loading={removeMember.isPending && removeMember.variables === member.id}
                  onClick={() => handleRemoveMember(member.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </LoadingButton>
              </div>
            ))
          ) : (
            <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
              No members
            </div>
          )}
        </div>
      </div>

        </TabsContent>
      </Tabs>
    </div>
  )
}
